import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Table,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import Excel from 'assets/icons/svg/Excel';
import RatingStarIcon from 'assets/icons/svg/RatingStarIcon';
import { getBackgroundColorByRatingSum } from 'pages/PagePartnersRatingReport/helpers';
import { IPartnerRatingByPartner, IServiceType } from '../../../interfaces';
import { PartnerRatingDialog } from '../../../components';
import { useExportPartnerRatingByPartnerExcelReport } from '../../../hooks';

const useStyles = makeStyles({
  tableHeadCell: {
    backgroundColor: '#f5f5f5',
  },
  tableHeadCellSticky: {
    position: 'sticky',
    backgroundColor: '#f5f5f5',
  },
  tableBodyRow: {
    cursor: 'pointer',

    '&:nth-of-type(odd) td': {
      backgroundColor: '#f9f9f9',
    },
    '&:nth-of-type(even) td': {
      backgroundColor: '#fff',
    },
  },
  tableBodyCellSticky: {
    position: 'sticky',
  },
});

interface IProps {
  serviceType: IServiceType;
  isActivePartner: boolean;
  data: { list: IPartnerRatingByPartner[]; totalCount: number };
  year: number;
  loading: boolean;
  success: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  exportExcelLoading: boolean;
  onExportExcel(): void;
  onChangePage(pageNo: number): void;
}

const PartnersRating = ({
  serviceType,
  isActivePartner,
  data,
  year,
  error,
  exportExcelLoading,
  loading,
  page,
  pageSize,
  success,
  onChangePage,
  onExportExcel,
}: IProps) => {
  // Styles
  const classes = useStyles();

  const [selectedPartnerRating, setSelectedPartnerRating] = useState<IPartnerRatingByPartner | null>(null);
  const [partnerRatingDialogOpened, setOpenedPartnerRatingDialog] = useState(false);

  const { exportPartnerRatingByPartnerExcelReport, ...partnerRatingByPartnerExcelState } =
    useExportPartnerRatingByPartnerExcelReport();

  const params = {
    partnerGuid: selectedPartnerRating?.partnerGuid,
    serviceTypeId: serviceType.serviceTypeId,
    year,
  };

  function handleClickPartnerRating(partnerRating: IPartnerRatingByPartner) {
    setSelectedPartnerRating(partnerRating);
    setOpenedPartnerRatingDialog(true);
  }

  function handleClosePartnerRatingDialog() {
    setSelectedPartnerRating(null);
    setOpenedPartnerRatingDialog(false);
  }

  return (
    <>
      {Boolean(loading) ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : Boolean(error) && !Boolean(data.list.length) ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Typography component="h3" variant="h5" align="center">
            {error}
          </Typography>
        </Box>
      ) : (
        success && (
          <>
            {data.list.length ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <Box
                        component={TableCell}
                        className={classes.tableHeadCellSticky}
                        style={{ minWidth: 200, left: 0 }}
                      >
                        <b>Оценка</b>
                      </Box>

                      {(isActivePartner
                        ? [
                            ['Партнер', 220, 200],
                            ['Кол-во ЖК', 160, 420, 'right'],
                            ['Место в рейтинге', 150, 580, 'right'],
                          ]
                        : [
                            ['Партнер', 220, 200],
                            ['Кол-во ЖК', 160, 420, 'right'],
                          ]
                      ).map(([label, width, offset, textAlign]) => (
                        <Box
                          key={label}
                          component={TableCell}
                          className={classes.tableHeadCellSticky}
                          // @ts-ignore
                          style={{ minWidth: width, left: offset, textAlign }}
                        >
                          <b>{label}</b>
                        </Box>
                      ))}

                      {['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'].map(
                        (month) => (
                          <TableCell
                            key={month}
                            className={classes.tableHeadCell}
                            style={{ minWidth: 100 }}
                            align="center"
                          >
                            <b>{month}</b>
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.list.map((partnerRating, index) => {
                      const { ratingPosition, partnerName, realEstateCount, ratingSum, partnerGuid, months } =
                        partnerRating;
                      const backgroundColor = isActivePartner ? getBackgroundColorByRatingSum(ratingSum) : '#FFF';

                      return (
                        <TableRow
                          key={`${partnerGuid}:${index}`}
                          className={classes.tableBodyRow}
                          hover
                          onClick={() => handleClickPartnerRating(partnerRating)}
                        >
                          <TableCell
                            className={classes.tableHeadCellSticky}
                            style={{ textAlign: 'center', minWidth: 200, left: 0 }}
                          >
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gridGap: 8 }}
                            >
                              <Rating icon={<RatingStarIcon />} value={ratingSum} readOnly precision={0.25} />
                              <b>{ratingSum}</b>
                            </Box>
                          </TableCell>

                          {(isActivePartner
                            ? [
                                [partnerName, 200],
                                [realEstateCount, 420, 'right'],
                                [ratingPosition, 580, 'right'],
                              ]
                            : [
                                [partnerName, 200],
                                [realEstateCount, 420, 'center'],
                              ]
                          ).map(([value, offset, textAlign], index) => (
                            <TableCell
                              key={index}
                              className={classes.tableBodyCellSticky}
                              style={{
                                // @ts-ignore
                                textAlign,
                                left: offset,
                                ...(isActivePartner && { backgroundColor }),
                              }}
                            >
                              {value}
                            </TableCell>
                          ))}

                          {[...Array(12)].map((_, index) => {
                            const month = months.find((month) => +month.dateAt.split('-')[1] === index + 1);

                            return month ? (
                              <TableCell style={{ textAlign: 'center' }} key={month.dateAt}>
                                {Math.round(month.ratingSum * 100) / 100}
                              </TableCell>
                            ) : (
                              <TableCell key={index} style={{ textAlign: 'center' }}>
                                0
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography component="h3" variant="h5" align="center">
                  Данные отсутствуют
                </Typography>
              </Box>
            )}

            <PartnerRatingDialog
              partnerGuid={selectedPartnerRating?.partnerGuid ?? ''}
              partnerName={selectedPartnerRating?.partnerName ?? ''}
              partnersCount={data.totalCount}
              year={year}
              serviceType={serviceType}
              isActivePartner={isActivePartner}
              open={partnerRatingDialogOpened}
              exportExcelLoading={partnerRatingByPartnerExcelState.loading}
              onExportExcel={() =>
                exportPartnerRatingByPartnerExcelReport(selectedPartnerRating!.partnerGuid, { params })
              }
              onClose={handleClosePartnerRatingDialog}
            />
          </>
        )
      )}

      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {Boolean(data.totalCount) && (
          <Tooltip title="Скачать отчет в Excel">
            <span>
              <IconButton
                aria-label="Скачать отчет в Excel"
                onClick={onExportExcel}
                disabled={loading || exportExcelLoading}
              >
                <Excel style={{ color: '#3C763D' }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <IconButton aria-label="Назад" disabled={loading || page === 1} onClick={() => onChangePage(page - 1)}>
          <KeyboardArrowLeft />
        </IconButton>

        <IconButton
          aria-label="Вперёд"
          disabled={loading || !data.totalCount || page === Math.ceil((data.totalCount || 0) / pageSize)}
          onClick={() => onChangePage(page + 1)}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </>
  );
};

export default PartnersRating;
